/**
 * Page de callback OAuth2 Keycloak
 * Gère le retour depuis Keycloak après authentification
 * Échange le code d'autorisation contre un access token
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { exchangeCodeForTokens, getUserFromToken, forceLogoutRedirect } from '@/services/keycloak';
import { Spin, Alert, Card } from 'antd';

// Rôles autorisés à accéder au backoffice. VIEWER (abonnés mobiles) est exclu.
const ALLOWED_BACKOFFICE_ROLES = ['ADMIN', 'EDITOR', 'AUTHOR', 'REVIEWER', 'SUPER_ADMIN'];
import { LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

export const CallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Vérifier s'il y a une erreur dans les params
      const errorParam = searchParams.get('error');
      if (errorParam) {
        const errorDescription = searchParams.get('error_description') || 'Une erreur est survenue lors de la connexion';
        setStatus('error');
        setError(errorDescription);

        // Rediriger vers login en signalant l'erreur (évite la boucle de redirection)
        setTimeout(() => {
          navigate('/login?auth_error=' + encodeURIComponent(errorDescription), { replace: true });
        }, 2000);
        return;
      }

      // Récupérer le code d'autorisation
      const code = searchParams.get('code');
      if (!code) {
        setStatus('error');
        setError('Code d\'autorisation manquant');

        setTimeout(() => {
          navigate('/login?auth_error=' + encodeURIComponent('Code d\'autorisation manquant'), { replace: true });
        }, 2000);
        return;
      }

      try {
        // Échanger le code contre des tokens
        console.log('Exchanging authorization code for tokens...');
        await exchangeCodeForTokens(code);

        // Garde de rôle : rejeter les comptes sans rôle backoffice (ex : VIEWER).
        const user = getUserFromToken();
        const authorized = (user?.roles || []).some((r) =>
          ALLOWED_BACKOFFICE_ROLES.includes(String(r).toUpperCase())
        );
        if (!authorized) {
          sessionStorage.removeItem('kc_last_redirect');
          // Déconnexion complète (tokens + SSO) + message sur la page de login.
          forceLogoutRedirect(
            '/login?auth_error=' +
              encodeURIComponent("Accès refusé : votre compte n'a pas les droits pour accéder au backoffice.")
          );
          return;
        }

        // Succès! On efface le marqueur anti-boucle.
        sessionStorage.removeItem('kc_last_redirect');
        setStatus('success');

        // Rediriger vers le dashboard après 1 seconde
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } catch (err: any) {
        console.error('Token exchange failed:', err);
        setStatus('error');
        const msg = err.message || 'Échec de l\'échange du code d\'autorisation';
        setError(msg);

        setTimeout(() => {
          navigate('/login?auth_error=' + encodeURIComponent(msg), { replace: true });
        }, 2000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Card
        style={{
          width: 400,
          textAlign: 'center',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}
      >
        {status === 'loading' && (
          <>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
              style={{ marginBottom: 24 }}
            />
            <h2 style={{ marginBottom: 8 }}>Authentification en cours...</h2>
            <p style={{ color: '#666', marginBottom: 0 }}>
              Veuillez patienter pendant que nous finalisons votre connexion.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleOutlined
              style={{
                fontSize: 48,
                color: '#52c41a',
                marginBottom: 24,
              }}
            />
            <h2 style={{ marginBottom: 8, color: '#52c41a' }}>Connexion réussie!</h2>
            <p style={{ color: '#666', marginBottom: 0 }}>
              Redirection vers le dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <CloseCircleOutlined
              style={{
                fontSize: 48,
                color: '#ff4d4f',
                marginBottom: 24,
              }}
            />
            <h2 style={{ marginBottom: 16, color: '#ff4d4f' }}>Erreur d'authentification</h2>
            <Alert
              message={error || 'Une erreur est survenue'}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <p style={{ color: '#666', fontSize: 14 }}>
              Redirection vers la page de connexion...
            </p>
          </>
        )}
      </Card>
    </div>
  );
};

export default CallbackPage;
